import React, { useState } from "react";
import { Container, Card, Button, Alert, Spinner, ProgressBar, Badge } from "react-bootstrap";
import { FaCalendarCheck, FaCogs, FaSave, FaTimes, FaCheckCircle } from "react-icons/fa";
import Swal from "sweetalert2";

// Hooks
import { useOcurrencias } from "./hooks/useOcurrencias";
import { useGeneradorOcurrencias } from "./hooks/useGeneradorOcurrencias";

// Components
import OcurrenciasFiltros from "./components/OcurrenciasFiltros";
import OcurrenciasStats from "./components/OcurrenciasStats";
import OcurrenciasTabla from "./components/OcurrenciasTabla";
import ModalGenerar from "./components/ModalGenerar";
import ModalDetalle from "./components/ModalDetalle";

const VisualizarOcurrencias = ({ usuario }) => {
    const {
        ocurrencias,
        empleados,
        loading,
        filtros,
        setFiltros,
        cargarOcurrencias,
        eliminarOcurrencia,
        actualizarOcurrencia,
        limpiarFiltros,
    } = useOcurrencias(usuario);

    const {
        generandoOcurrencias,
        progreso,
        showGenerarModal,
        setShowGenerarModal,
        parametrosGeneracion,
        setParametrosGeneracion,
        analizarOcurrencias,
        guardarOcurrencias,
    } = useGeneradorOcurrencias(usuario, empleados, (periodo) => {
        if (filtros.periodo === periodo) {
            cargarOcurrencias();
        }
    });

    const [modoEdicion, setModoEdicion] = useState(false);
    const [ocurrenciaSeleccionada, setOcurrenciaSeleccionada] = useState(null);
    const [showDetalleModal, setShowDetalleModal] = useState(false);

    // Preview state
    const [modoPreview, setModoPreview] = useState(false);
    const [ocurrenciasPreview, setOcurrenciasPreview] = useState([]);
    const [seleccionados, setSeleccionados] = useState({});

    const handleVer = (ocurrencia) => {
        setOcurrenciaSeleccionada(ocurrencia);
        setModoEdicion(false);
        setShowDetalleModal(true);
    };

    const handleEditar = (ocurrencia) => {
        setOcurrenciaSeleccionada(ocurrencia);
        setModoEdicion(true);
        setShowDetalleModal(true);
    };

    const handleGuardarCambios = async (datosActualizados) => {
        const exito = await actualizarOcurrencia(datosActualizados);
        if (exito) {
            setShowDetalleModal(false);
        }
    };

    const handleAnalizar = async (file) => {
        const resultados = await analizarOcurrencias(file);
        if (resultados && resultados.length > 0) {
            setOcurrenciasPreview(resultados);
            const initialSel = {};
            resultados.forEach((r, i) => initialSel[i] = true);
            setSeleccionados(initialSel);
            setModoPreview(true);
            setShowGenerarModal(false);
        } else if (resultados) {
            Swal.fire({
                icon: "info",
                title: "Sin resultados",
                text: "No se detectaron nuevas ocurrencias para el periodo y filtros indicados.",
                confirmButtonColor: "#8B4513"
            });
        }
    };

    const handleCancelarPreview = () => {
        setModoPreview(false);
        setOcurrenciasPreview([]);
        setSeleccionados({});
    };

    const handleGuardarPreview = async () => {
        const porGuardar = ocurrenciasPreview.filter((_, i) => seleccionados[i]);
        if (porGuardar.length === 0) {
            return;
        }
        await guardarOcurrencias(porGuardar);
        setModoPreview(false);
        setOcurrenciasPreview([]);
        setSeleccionados({});
    };

    const toggleSeleccion = (index) => {
        setSeleccionados(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const toggleAll = (checked) => {
        const newSel = {};
        ocurrenciasPreview.forEach((_, i) => newSel[i] = checked);
        setSeleccionados(newSel);
    };

    // Determinar qué datos mostrar
    const datosAMostrar = modoPreview ? ocurrenciasPreview : ocurrencias;
    const countSeleccionados = Object.values(seleccionados).filter(Boolean).length;

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header Premium con gradiente marrón */}
                    <Card.Header
                        className="text-white py-4"
                        style={{
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            borderBottom: "3px solid #D2691E",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="fw-bold mb-1">
                                    <FaCalendarCheck className="me-3" />
                                    Gestión de Ocurrencias de Planilla
                                </h2>
                                <p className="mb-0 opacity-75">
                                    {modoPreview
                                        ? `Previsualización: ${ocurrenciasPreview.length} ocurrencias detectadas`
                                        : "Visualización y generación automática de incidencias laborales"
                                    }
                                </p>
                            </div>
                            <div className="d-flex gap-2">
                                {modoPreview ? (
                                    <>
                                        <Button
                                            variant="light"
                                            onClick={handleCancelarPreview}
                                            disabled={generandoOcurrencias}
                                            style={{
                                                borderRadius: "10px",
                                                padding: "10px 20px",
                                            }}
                                        >
                                            <FaTimes className="me-2" />
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handleGuardarPreview}
                                            disabled={generandoOcurrencias || countSeleccionados === 0}
                                            style={{
                                                borderRadius: "10px",
                                                background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                                border: "none",
                                                color: "white",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                padding: "10px 20px",
                                            }}
                                        >
                                            <FaSave className="me-2" />
                                            Guardar ({countSeleccionados})
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="light"
                                        onClick={() => {
                                            setParametrosGeneracion(prev => ({
                                                ...prev,
                                                periodo: filtros.periodo || prev.periodo
                                            }));
                                            setShowGenerarModal(true);
                                        }}
                                        disabled={generandoOcurrencias}
                                        style={{
                                            borderRadius: "10px",
                                            background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                            border: "none",
                                            color: "white",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                            padding: "10px 20px",
                                        }}
                                        className="d-flex align-items-center gap-2"
                                    >
                                        <FaCogs size={16} />
                                        Generar Ocurrencias
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Progreso de generación */}
                        {generandoOcurrencias && (
                            <Card
                                className="shadow-sm border-0 mb-4"
                                style={{ borderRadius: "15px" }}
                            >
                                <Card.Body className="p-4 text-center">
                                    <div
                                        className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "60px",
                                            height: "60px",
                                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                                            color: "white"
                                        }}
                                    >
                                        <Spinner animation="border" variant="light" size="sm" />
                                    </div>
                                    <h5 className="mb-2 fw-bold" style={{ color: "#8B4513" }}>
                                        {progreso.mensaje || "Procesando..."}
                                    </h5>
                                    <ProgressBar
                                        now={progreso.total > 0 ? (progreso.actual / progreso.total) * 100 : 0}
                                        label={`${progreso.actual}/${progreso.total}`}
                                        variant="warning"
                                        animated
                                        style={{ height: "20px", borderRadius: "10px" }}
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        {/* Alert de modo preview */}
                        {modoPreview && (
                            <Alert variant="warning" className="mb-4">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <FaCheckCircle className="me-2" />
                                        <strong>Modo Previsualización:</strong> Revisa y selecciona las ocurrencias que deseas guardar.
                                        Método: <Badge bg="info">{parametrosGeneracion.metodo === 'excel' ? 'EXCEL' : 'AUTOMÁTICO'}</Badge>
                                    </div>
                                    <div className="text-end">
                                        <small className="text-muted">
                                            {countSeleccionados} de {ocurrenciasPreview.length} seleccionados
                                        </small>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {/* Filtros */}
                        <Card
                            className="shadow-sm border-0 mb-4"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Header
                                className="text-white py-3"
                                style={{
                                    background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                                    borderRadius: "15px 15px 0 0",
                                }}
                            >
                                <h5 className="mb-0 fw-semibold">Filtros de Búsqueda</h5>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <OcurrenciasFiltros
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    empleados={empleados}
                                    onBuscar={cargarOcurrencias}
                                    loading={loading}
                                    limpiarFiltros={limpiarFiltros}
                                />
                            </Card.Body>
                        </Card>

                        {/* Estadísticas */}
                        <OcurrenciasStats ocurrencias={modoPreview ? ocurrenciasPreview : ocurrencias} />

                        {/* Tabla */}
                        <Card
                            className="shadow-sm border-0"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Body className="p-4">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" style={{ color: "#8B4513" }} />
                                        <p className="mt-3" style={{ color: "#8B4513" }}>
                                            Cargando ocurrencias...
                                        </p>
                                    </div>
                                ) : (
                                    <OcurrenciasTabla
                                        ocurrencias={datosAMostrar}
                                        empleados={empleados}
                                        filtros={filtros}
                                        onVer={handleVer}
                                        onEditar={handleEditar}
                                        onEliminar={eliminarOcurrencia}
                                        modoPreview={modoPreview}
                                        seleccionados={seleccionados}
                                        onToggleSeleccion={toggleSeleccion}
                                        onToggleAll={toggleAll}
                                    />
                                )}
                            </Card.Body>
                        </Card>
                    </Card.Body>
                </Card>

                <ModalGenerar
                    show={showGenerarModal}
                    onHide={() => setShowGenerarModal(false)}
                    generando={generandoOcurrencias}
                    parametros={parametrosGeneracion}
                    setParametros={setParametrosGeneracion}
                    onAnalizar={handleAnalizar}
                />

                <ModalDetalle
                    show={showDetalleModal}
                    onHide={() => setShowDetalleModal(false)}
                    ocurrencia={ocurrenciaSeleccionada}
                    modoEdicion={modoEdicion}
                    onGuardar={handleGuardarCambios}
                    empleados={empleados}
                />
            </Container>
        </div>
    );
};

export default VisualizarOcurrencias;
